import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@client/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@client/components/ui/select";
import { Label } from "@client/components/ui/label";

export const Route = createFileRoute("/_authenticated/recovery-requests")({
  component: RecoveryRequests,
});

function RecoveryRequests() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["recovery-requests"],
    queryFn: staffApi.recoveryRequests,
  });

  const { data: instructorsData } = useQuery({
    queryKey: ["instructors"],
    queryFn: staffApi.instructors,
  });

  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [resolveAction, setResolveAction] = useState<"RESOLVED" | "REJECTED">(
    "RESOLVED",
  );
  const [selectedInstructorId, setSelectedInstructorId] = useState("");
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const resolveMutation = useMutation({
    mutationFn: (args: {
      requestId: string;
      status: "RESOLVED" | "REJECTED";
    }) => staffApi.resolveRecoveryRequest(args.requestId, args.status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recovery-requests"] });
      if (resolveAction === "REJECTED") {
        toast.success("Request rejected.");
        setSelectedRequest(null);
      }
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to resolve request.");
    },
  });

  const resetMutation = useMutation({
    mutationFn: (instructorId: string) =>
      staffApi.resetInstructorPassword(instructorId),
    onSuccess: (data) => {
      setTempPassword(data.tempPassword);
      toast.success(
        "Password reset successfully. A temporary password was generated.",
      );
      // Also resolve the request if one is selected
      if (selectedRequest) {
        resolveMutation.mutate({
          requestId: selectedRequest.id,
          status: "RESOLVED",
        });
      }
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to reset password.");
    },
  });

  const handleResolveSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (resolveAction === "REJECTED") {
      resolveMutation.mutate({
        requestId: selectedRequest.id,
        status: "REJECTED",
      });
    } else {
      if (!selectedInstructorId) {
        toast.error("Please select an instructor to reset their password.");
        return;
      }
      resetMutation.mutate(selectedInstructorId);
    }
  };

  const closeDialog = () => {
    setSelectedRequest(null);
    setTempPassword(null);
    setSelectedInstructorId("");
    setResolveAction("RESOLVED");
  };

  return (
    <StaffShell
      title="Recovery Requests"
      subtitle="Manage credential recovery requests from staff members"
    >
      <div className="panel p-5">
        <h2 className="hall-heading text-lg font-bold">Pending Requests</h2>
        {isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="mt-4 space-y-3">
            {data?.requests
              .filter((r) => r.status === "PENDING")
              .map((req) => (
                <div
                  key={req.id}
                  className="flex items-center justify-between rounded-md border border-border p-4"
                >
                  <div>
                    <p className="font-semibold text-foreground">
                      {req.provided_name || "Unknown Name"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Email provided: {req.provided_email || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Request ID: {req.public_id} • Requested at:{" "}
                      {new Date(req.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedRequest(req)}
                  >
                    Resolve
                  </Button>
                </div>
              ))}
            {data?.requests.filter((r) => r.status === "PENDING").length ===
              0 && (
              <p className="text-sm text-muted-foreground">
                No pending requests.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="panel mt-6 p-5">
        <h2 className="hall-heading text-lg font-bold">Past Requests</h2>
        <div className="mt-4 space-y-2">
          {data?.requests
            .filter((r) => r.status !== "PENDING")
            .map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between rounded-md border border-border p-3 opacity-70"
              >
                <div>
                  <p className="text-sm font-medium">
                    {req.provided_name} ({req.provided_email})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {req.public_id} • Resolved by: {req.resolved_by_email} at{" "}
                    {req.resolved_at
                      ? new Date(req.resolved_at).toLocaleString()
                      : ""}
                  </p>
                </div>
                <Badge
                  variant={
                    req.status === "RESOLVED" ? "default" : "destructive"
                  }
                >
                  {req.status}
                </Badge>
              </div>
            ))}
        </div>
      </div>

      <Dialog
        open={!!selectedRequest}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Recovery Request</DialogTitle>
            <DialogDescription>
              Review the request for{" "}
              <strong>{selectedRequest?.provided_name}</strong> (
              {selectedRequest?.provided_email}).
            </DialogDescription>
          </DialogHeader>

          {tempPassword ? (
            <div className="space-y-4 py-4">
              <div className="rounded-md bg-green-500/10 p-4 border border-green-500/20">
                <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                  Password Reset Successful
                </p>
                <p className="mt-2 text-sm">
                  Please securely communicate this temporary password to the
                  instructor.
                  <strong> It will only be shown once.</strong>
                </p>
                <div className="mt-4 p-3 bg-card border rounded flex items-center justify-center">
                  <span className="font-mono text-xl tracking-wider select-all">
                    {tempPassword}
                  </span>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleResolveSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Action</Label>
                <Select
                  value={resolveAction}
                  onValueChange={(val: any) => setResolveAction(val)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RESOLVED">
                      Approve & Reset Password
                    </SelectItem>
                    <SelectItem value="REJECTED">Reject Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {resolveAction === "RESOLVED" && (
                <div className="space-y-2">
                  <Label>Target Instructor Account</Label>
                  <Select
                    value={selectedInstructorId}
                    onValueChange={setSelectedInstructorId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select instructor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {instructorsData?.instructors.map((inst) => (
                        <SelectItem key={inst.id} value={inst.id}>
                          {inst.full_name} ({inst.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select the actual account to reset. A temporary password
                    will be generated.
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    resolveMutation.isPending || resetMutation.isPending
                  }
                >
                  Confirm
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
